/**
 * Mappen und Wochenplan in der App (S62, Stufe 2; 25-zeitsteuerung.md § 5 Z5–Z7, § 6, Bild 6–13):
 * under „Im Netz“ what applies now and tomorrow, the folders, the plan, the exceptions; the flow
 * „Mappen › Wochenplan › Prüfen“; a day that is different; the tile „Heute“ on „Karten“. Only with
 * „Erweitert“; owner and manager change it, the service sees it and plans tomorrow's recommendation.
 */
import { useMemo, useState } from "react";
import { OTHERWISE, PLAN_HOUR, TIME_WORDS, atLocal, checkSchedule, clockText, dateAfter, dayPlan, dayWords, editsMenus, folderAt, localOf, publishes, weekdayOf } from "@speisekarte/core";
import type { DayTime, DrinksFrom, Exception, Folder, Schedule, Weekday } from "@speisekarte/core";
import { folderOld, outdated } from "../outside.ts";
import { printMenu, printMenuOf } from "../output.ts";
import { api, messageOf, store, useStore } from "../store.ts";
import type { Data } from "../store.ts";
import { uhr, whenText } from "../timing.ts";
import { minutesOf } from "./Times.tsx";

const DAYS = TIME_WORDS.de.days;
const DAYS_LONG = ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag", "Sonntag"];
const EMPTY: Schedule = { rules: [], closed: [] };

type DataFolder = NonNullable<Data["folders"]>[number];

/** The folders with „Sonst“ first: what the app names and shows of each. */
export function foldersOf(data: Data) {
  const pub = data.published ? data.menus.find((m) => m.id === data.published!.menu) : undefined;
  const otherwise = { id: OTHERWISE, name: data.settings.otherwiseName || "Abend", menu: pub?.id ?? "", recommendations: data.settings.recommendationsMenu ?? "", drinksFrom: (data.settings.drinksFrom ?? "") as DrinksFrom, drinksMenu: data.settings.drinksMenu, tone: -1 };
  return [otherwise, ...(data.folders ?? [])];
}

const toneOf = (id: string, data: Data) => (id === OTHERWISE ? "tone-s" : `tone-${data.folders?.find((f) => f.id === id)?.tone ?? 0}`);
const nameOf = (id: string, data: Data) => (id === OTHERWISE ? data.settings.otherwiseName || "Abend" : (data.folders?.find((f) => f.id === id)?.name ?? "?"));
const titleOf = (id: string, data: Data) => data.menus.find((m) => m.id === id)?.title ?? "";

/** A chip of a folder in its colour, always with its name (§ 6.1, 2). */
export const FolderChip = ({ id, data }: { id: string; data: Data }) => <span className={`folder-chip ${toneOf(id, data)}`}>{nameOf(id, data)}</span>;

/** The bar of a day of the plan: the folders in their times, „Sonst“ between them. */
export function PlanBar({ data, date, minute, label }: { data: Data; date: string; minute?: number; label: string }) {
  const plan = dayPlan(data.settings.schedule ?? EMPTY, data.settings.exceptions ?? [], date);
  return (
    <div className="day-bar" data-testid="plan-bar">
      <div className="day-bar-label">
        {label}
        {plan.closed && " · Ruhetag"}
        {plan.exception && !plan.closed && " · Ausnahme"}
      </div>
      <div className="day-bar-track">
        <span className="day-bar-seg tone-s" style={{ left: 0, width: "100%", justifyContent: "flex-start", paddingLeft: "0.4rem" }}>
          {nameOf(OTHERWISE, data)}
        </span>
        {plan.pieces.map((p, k) => (
          <span key={k} className={`day-bar-seg ${toneOf(p.folder, data)}`} style={{ left: `${(p.from / 1440) * 100}%`, width: `${((p.to - p.from) / 1440) * 100}%` }} title={`${nameOf(p.folder, data)} ${uhr(p.from)}–${uhr(p.to % 1440)}`}>
            {nameOf(p.folder, data)}
          </span>
        ))}
        {minute !== undefined && <span className="day-bar-now" style={{ left: `${(minute / 1440) * 100}%` }} aria-hidden="true" />}
      </div>
      <div className="day-bar-ticks" aria-hidden="true">
        <span>0</span>
        <span>6</span>
        <span>12</span>
        <span>18</span>
        <span>24</span>
      </div>
    </div>
  );
}

/** Jetzt and next in words (§ 6.1, 1): „Jetzt: Mittag bis 14:30 · Als Nächstes: 17:00 Abend“. */
export function nowWords(data: Data, at = Date.now()) {
  const now = folderAt(data.settings.schedule ?? EMPTY, data.settings.exceptions ?? [], at);
  return { ...now, text: `${nameOf(now.folder, data)}${now.closed ? " (heute Ruhetag)" : ""}${now.until ? ` bis ${whenText(now.until, at)}` : ""}`, next: now.next ? `${whenText(now.next.at, at)} ${nameOf(now.next.folder, data)}` : "" };
}

/** A day in words (Bild 8): „11:30–14:30 Mittag · 17:00–23:00 Abend · sonst Abend“. */
const dayText = (data: Data, date: string) => {
  const p = dayPlan(data.settings.schedule ?? EMPTY, data.settings.exceptions ?? [], date);
  return dayWords(p.pieces, (f) => nameOf(f, data), p.closed);
};

/** „Ausnahme“ in words: „24.12.: nur Mittag“. */
function exceptionText(e: Exception, data: Data): string {
  const [, m, d] = e.date.split("-");
  const day = `${DAYS[weekdayOf(e.date)]} ${Number(d)}.${Number(m)}.`;
  if (e.kind === "closed") return `${day}: geschlossen`;
  if (e.kind === "like") return `${day}: wie ${DAYS_LONG[e.like]}`;
  if (e.kind === "only") return `${day}: den ganzen Tag ${nameOf(e.folder, data)}`;
  return `${day}: ${e.times.map((t) => `${uhr(t.from)}–${uhr(t.to % 1440)} ${nameOf(t.folder, data)}`).join(" · ") || "nur Sonst"}`;
}

/**
 * Im Netz with „Erweitert“ (Bild 6, 10): before the first folder the way in; then Jetzt, the bar of
 * today, Morgen with its recommendations, the folders, the plan, the exceptions.
 */
export function PlanCard({ data }: { data: Data }) {
  const role = useStore().role;
  const keeper = publishes(role);
  const [state, setState] = useState<{ busy?: boolean; ok?: string; error?: string }>({});
  const [except, setExcept] = useState(false);
  const at = Date.now();
  const today = localOf(at);
  const tomorrow = dateAfter(today.date, 1);
  const folders = data.folders ?? [];
  async function run(fn: () => Promise<unknown>, ok: string) {
    setState({ busy: true });
    try {
      await store.change(fn);
      setState({ ok });
    } catch (err) {
      setState({ error: messageOf(err) });
    }
  }
  if (!folders.length)
    return (
      <section className="card" data-testid="plan-card">
        <h2>Wochenplan</h2>
        <p className="hint">Zu verschiedenen Zeiten verschiedene Karten zeigen: mittags die Mittagskarte, abends die Hauptkarte, mit Ruhetag und Ausnahmen. Die jetzige Einstellung wird die erste Mappe und gilt sonst.</p>
        {keeper ? (
          <a className="button primary" href="#/wochenplan" data-testid="plan-start">
            Wochenplan einrichten …
          </a>
        ) : (
          <p className="small">Einrichten können Inhaber und Verwaltung.</p>
        )}
      </section>
    );
  const now = nowWords(data, at);
  return (
    <section className="card" data-testid="plan-card">
      <h2>Wochenplan</h2>
      <div className="now-line" data-testid="plan-now">
        <p style={{ margin: 0 }}>
          <b>Jetzt:</b> <FolderChip id={now.folder} data={data} /> {now.text.slice(nameOf(now.folder, data).length)}
        </p>
        {now.next && (
          <p className="muted" style={{ margin: "0.1rem 0 0" }} data-testid="plan-next">
            Als Nächstes: {now.next}
          </p>
        )}
        <PlanBar data={data} date={today.date} minute={today.minute} label={`Heute, ${DAYS_LONG[today.weekday]}`} />
      </div>
      <Tomorrow data={data} date={tomorrow} />
      <h3 className="lab">Mappen</h3>
      <ul className="folder-list" data-testid="plan-folders">
        {foldersOf(data).map((f) => {
          const old = f.id === OTHERWISE ? outdated(data) : folderOld(f as DataFolder, data);
          return (
            <li key={f.id} data-testid="plan-folder">
              <FolderChip id={f.id} data={data} />
              <span className="folder-what">
                {titleOf(f.menu, data) || "keine Speisekarte"}
                {f.recommendations && ` · Empfehlung: ${titleOf(f.recommendations, data)}`}
                {f.id === OTHERWISE && <span className="muted small"> · gilt sonst</span>}
              </span>
              {old && keeper && (
                <button className="primary small-button" disabled={state.busy} onClick={() => void run(() => api(f.id === OTHERWISE ? "/api/publish" : `/api/folders/${f.id}/publish`, { body: f.id === OTHERWISE ? { menu: f.menu } : {} }), `„${f.name}“ übernommen`)} data-testid="plan-folder-take">
                  ⚠ Änderungen übernehmen
                </button>
              )}
            </li>
          );
        })}
      </ul>
      <h3 className="lab">Woche</h3>
      <ul className="week-words" data-testid="plan-week">
        {Array.from({ length: 7 }, (_, k) => dateAfter(today.date, k)).map((date) => (
          <li key={date}>
            <b>{DAYS[weekdayOf(date)]}</b> {dayText(data, date)}
          </li>
        ))}
      </ul>
      {(data.settings.exceptions ?? []).length > 0 && (
        <>
          <h3 className="lab">Ausnahmen</h3>
          <ul className="week-words" data-testid="plan-exceptions">
            {(data.settings.exceptions ?? []).map((e) => (
              <li key={e.date}>
                {exceptionText(e, data)}
                {keeper && (
                  <button className="link danger" onClick={() => void run(() => api("/api/schedule", { method: "PUT", body: { exceptions: (data.settings.exceptions ?? []).filter((x) => x.date !== e.date) } }), "Ausnahme entfernt")} aria-label="Ausnahme entfernen" data-testid="plan-exception-remove">
                    ✕
                  </button>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
      {keeper && (
        <div className="buttons">
          <a className="button" href="#/wochenplan" data-testid="plan-change">
            Wochenplan ändern
          </a>
          <button onClick={() => setExcept(true)} data-testid="plan-exception-add">
            Ausnahme für einen Tag …
          </button>
          <a className="button" href={`/api/publish/preview?at=${at}&workspace=${store.session!.workspaceId}`} target="_blank" rel="noopener">
            So sehen Gäste es jetzt
          </a>
        </div>
      )}
      {state.ok && <p className="ok-text">{state.ok}</p>}
      {state.error && <p className="error">{state.error}</p>}
      {except && <ExceptionSheet data={data} onClose={() => setExcept(false)} />}
    </section>
  );
}

/**
 * Morgen (Z7, Bild 10): tomorrow in words, and for each folder of tomorrow its recommendation for
 * tomorrow – planned at its time; the service may too. The inserts for tomorrow to print.
 */
function Tomorrow({ data, date }: { data: Data; date: string }) {
  const role = useStore().role;
  const plan = dayPlan(data.settings.schedule ?? EMPTY, data.settings.exceptions ?? [], date);
  const firstOf = new Map<string, number>();
  for (const p of plan.pieces) if (!firstOf.has(p.folder)) firstOf.set(p.folder, p.from);
  if (!firstOf.has(OTHERWISE)) firstOf.set(OTHERWISE, PLAN_HOUR.recommendations);
  const [chosen, setChosen] = useState<Record<string, string>>({});
  const [state, setState] = useState<{ ok?: string; error?: string }>({});
  const planned = (data.settings.planned ?? []).filter((p) => p.what === "recommendations");
  const recOf = (f: string) => (f === OTHERWISE ? data.settings.recommendationsMenu : data.folders?.find((x) => x.id === f)?.recommendations) ?? "";
  async function planFor(folder: string) {
    const menu = chosen[folder];
    if (!menu) return;
    try {
      const at = atLocal(date, firstOf.get(folder)!);
      await store.change(() => api("/api/plan", { body: { what: "recommendations", menu, at, ...(folder ? { folder } : {}) } }));
      setState({ ok: `Geplant: ${whenText(at, Date.now())} „${titleOf(menu, data)}“ für ${nameOf(folder, data)}` });
    } catch (err) {
      setState({ error: messageOf(err) });
    }
  }
  const forTomorrow = [...firstOf.keys()].filter((f) => f === OTHERWISE || data.folders?.some((x) => x.id === f));
  const prints = forTomorrow.map((f) => planned.find((p) => (p.folder ?? "") === f && localOf(p.at).date === date)?.menu ?? recOf(f)).filter(Boolean);
  return (
    <div className="tomorrow" data-testid="plan-tomorrow">
      <p style={{ margin: "0.6rem 0 0.2rem" }}>
        <b>Morgen, {DAYS[weekdayOf(date)]}:</b> {dayText(data, date)}
      </p>
      {editsMenus(role) &&
        forTomorrow.map((f) => {
          const p = planned.find((x) => (x.folder ?? "") === f && localOf(x.at).date === date);
          return (
            <div key={f || "sonst"} className="tomorrow-rec" data-testid="plan-tomorrow-rec">
              <FolderChip id={f} data={data} />{" "}
              {p ? (
                <span>
                  Empfehlung ab {uhr(localOf(p.at).minute)}: „{titleOf(p.menu, data)}“
                </span>
              ) : (
                <>
                  <span className="muted small">Empfehlung für morgen:</span>
                  <select value={chosen[f] ?? ""} onChange={(e) => setChosen((c) => ({ ...c, [f]: e.target.value }))} aria-label={`Empfehlung für morgen, ${nameOf(f, data)}`} data-testid="plan-tomorrow-choice">
                    <option value="">{recOf(f) ? `wie jetzt: ${titleOf(recOf(f), data)}` : "keine"}</option>
                    {data.menus
                      .filter((m) => m.id !== (f === OTHERWISE ? data.published?.menu : data.folders?.find((x) => x.id === f)?.menu))
                      .map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.title}
                        </option>
                      ))}
                  </select>
                  <button disabled={!chosen[f]} onClick={() => void planFor(f)} data-testid="plan-tomorrow-plan">
                    Für morgen einplanen
                  </button>
                </>
              )}
            </div>
          );
        })}
      {prints.length > 0 && (
        <p className="buttons" style={{ marginTop: "0.4rem" }}>
          {[...new Set(prints)].map((id) => {
            const m = data.menus.find((x) => x.id === id);
            return m ? (
              <button key={id} onClick={() => void printMenu(printMenuOf(m, data, new Date(), undefined, m.id), data).catch((e) => setState({ error: messageOf(e) }))} data-testid="plan-tomorrow-print">
                Einlegeblatt für morgen drucken: {m.title}
              </button>
            ) : null;
          })}
        </p>
      )}
      {state.ok && <p className="ok-text">{state.ok}</p>}
      {state.error && <p className="error">{state.error}</p>}
    </div>
  );
}

/** Kachel „Heute“ (§ 6.1, 7; Bild 12): on „Karten“, only with „Erweitert“ and folders. */
export function TodayTile({ data }: { data: Data }) {
  if (data.settings.timing !== "extended" || !(data.folders ?? []).length) return null;
  const now = nowWords(data);
  const tomorrow = dateAfter(localOf(Date.now()).date, 1);
  return (
    <a className="today-tile" href="#/netz" data-testid="today-tile">
      <p style={{ margin: 0 }}>
        <b>Jetzt im Netz:</b> <FolderChip id={now.folder} data={data} /> {now.text.slice(nameOf(now.folder, data).length)}
        {now.next && <span className="muted"> · als Nächstes {now.next}</span>}
      </p>
      <p className="small" style={{ margin: "0.2rem 0 0" }}>
        <b>Morgen:</b> {dayText(data, tomorrow)}
      </p>
    </a>
  );
}

/** One day of the plan as the flow edits it: its times, closed or not. */
type DayRow = { times: DayTime[]; closed: boolean };

const rowsOf = (s: Schedule): DayRow[] => Array.from({ length: 7 }, (_, d) => ({ times: s.rules.filter((r) => r.window.days.includes(d as Weekday)).map((r) => ({ folder: r.folder, from: r.window.from, to: r.window.to })), closed: s.closed.includes(d as Weekday) }));

/** The rows back as a plan: the same time of the same folder on several days as one rule. */
function scheduleOf(rows: DayRow[]): Schedule {
  const groups = new Map<string, { folder: string; from: number; to: number; days: Weekday[] }>();
  rows.forEach((r, d) =>
    r.times.forEach((t) => {
      // The same time twice on one day stays two rules, so the overlap is seen (checkSchedule), not lost.
      let n = 0;
      while (groups.get(`${t.folder}|${t.from}|${t.to}|${n}`)?.days.includes(d as Weekday)) n++;
      const key = `${t.folder}|${t.from}|${t.to}|${n}`;
      const g = groups.get(key) ?? { ...t, days: [] };
      g.days.push(d as Weekday);
      groups.set(key, g);
    }),
  );
  return { rules: [...groups.values()].map((g) => ({ folder: g.folder, window: { days: g.days, from: g.from, to: g.to } })), closed: rows.flatMap((r, d) => (r.closed ? [d as Weekday] : [])) };
}

const STEPS = ["Mappen", "Wochenplan", "Prüfen"] as const;

/**
 * Wochenplan einrichten (Z5, Bild 7–9): Mappen › Wochenplan › Prüfen, as arrows (§ 6a), to leave at
 * any time. Folders change at once; the plan is kept on „Weiter“; „Fertig“ freezes every folder.
 */
export function PlanFlow() {
  const s = useStore();
  const data = s.data!;
  const [step, setStep] = useState(0);
  const [rows, setRows] = useState<DayRow[]>(() => rowsOf(data.settings.schedule ?? EMPTY));
  const [state, setState] = useState<{ busy?: boolean; error?: string }>({});
  const schedule = useMemo(() => scheduleOf(rows), [rows]);
  const wrong = checkSchedule(schedule, data.folders ?? []);
  if (!publishes(s.role) || data.settings.timing !== "extended")
    return (
      <section className="card">
        <h2>Wochenplan</h2>
        <p>Den Wochenplan richten Inhaber und Verwaltung ein, mit „Erweitert“ unter Konto → Zeitsteuerung.</p>
        <a className="button" href="#/netz">
          ← Im Netz
        </a>
      </section>
    );
  async function saveRows(next: number) {
    setState({ busy: true });
    try {
      await store.change(() => api("/api/schedule", { method: "PUT", body: { schedule } }));
      setState({});
      setStep(next);
    } catch (err) {
      setState({ error: messageOf(err) });
    }
  }
  async function done() {
    setState({ busy: true });
    try {
      await store.change(async () => {
        await api("/api/schedule", { method: "PUT", body: { schedule } });
        for (const f of data.folders ?? []) await api(`/api/folders/${f.id}/publish`, { body: {} });
      });
      location.hash = "#/netz";
    } catch (err) {
      setState({ error: messageOf(err) });
    }
  }
  return (
    <div className="plan-flow" data-testid="plan-flow">
      <p>
        <a href="#/netz" data-testid="plan-leave">
          ← Im Netz
        </a>
      </p>
      <ol className="step-arrows" data-testid="plan-steps">
        {STEPS.map((label, i) => (
          <li key={label} className={i < step ? "done" : i === step ? "now" : ""} aria-current={i === step ? "step" : undefined}>
            {i < step ? "✓ " : ""}
            {label}
          </li>
        ))}
      </ol>
      {step === 0 && <FoldersStep data={data} onNext={() => setStep(1)} />}
      {step === 1 && <WeekStep data={data} rows={rows} setRows={setRows} wrong={wrong} busy={Boolean(state.busy)} onBack={() => setStep(0)} onNext={() => void saveRows(2)} />}
      {step === 2 && <CheckStep data={data} schedule={schedule} wrong={wrong} busy={Boolean(state.busy)} onBack={() => setStep(1)} onDone={() => void done()} />}
      {state.error && <p className="error-box">{state.error}</p>}
    </div>
  );
}

/** Mappen (Bild 7): „Sonst“ is there already; a folder is a menu with its recommendations and drinks. */
function FoldersStep({ data, onNext }: { data: Data; onNext: () => void }) {
  const [adding, setAdding] = useState<{ name: string; menu: string } | null>((data.folders ?? []).length ? null : { name: "Mittag", menu: "" });
  const [state, setState] = useState<{ busy?: boolean; error?: string }>({});
  const [otherwise, setOtherwise] = useState(data.settings.otherwiseName || "Abend");
  async function run(fn: () => Promise<unknown>) {
    setState({ busy: true });
    try {
      await store.change(fn);
      setState({});
      return true;
    } catch (err) {
      setState({ error: messageOf(err) });
      return false;
    }
  }
  const pub = data.published ? data.menus.find((m) => m.id === data.published!.menu) : undefined;
  const menuOptions = data.menus.map((m) => (
    <option key={m.id} value={m.id}>
      {m.title}
    </option>
  ));
  const change = (f: Folder, c: Partial<Folder>) => void run(() => api(`/api/folders/${f.id}`, { method: "PATCH", body: c }));
  return (
    <section className="card" data-testid="plan-step-folders">
      <p className="hint" style={{ marginTop: 0 }}>
        Eine Mappe ist, was zusammen im Netz steht: eine Speisekarte, ihre Empfehlungskarte, ihre Getränke.
      </p>
      <div className="folder-box">
        <div className="folder-head">
          <span className="folder-chip tone-s">{otherwise || "Sonst"}</span>
          <span className="muted small">gilt sonst</span>
        </div>
        <label>
          Name
          <input value={otherwise} maxLength={40} onChange={(e) => setOtherwise(e.target.value)} onBlur={() => otherwise.trim() && otherwise !== data.settings.otherwiseName && void run(() => api("/api/schedule", { method: "PUT", body: { otherwiseName: otherwise } }))} data-testid="plan-otherwise-name" />
        </label>
        <p className="hint">
          Speisekarte: <b>{pub?.title ?? "keine"}</b>
          {data.settings.recommendationsMenu && ` · Empfehlung: ${titleOf(data.settings.recommendationsMenu, data)}`} · die jetzige Einstellung unter „Im Netz“
        </p>
      </div>
      {(data.folders ?? []).map((f) => (
        <div key={f.id} className="folder-box" data-testid="plan-folder-box">
          <div className="folder-head">
            <FolderChip id={f.id} data={data} />
            <span className="row" style={{ gap: "0.4rem" }}>
              <button className="link" onClick={() => confirm(`„${f.name}“ zu „Sonst“ machen? Sie tauscht mit „${data.settings.otherwiseName || "Abend"}“ den Platz.`) && void run(() => api(`/api/folders/${f.id}/otherwise`, { body: {} }))} data-testid="plan-folder-otherwise">
                Zu „Sonst“ machen
              </button>
              <button className="link danger" onClick={() => confirm(`Mappe „${f.name}“ löschen? Ihre Zeiten im Wochenplan gehen mit.`) && void run(() => api(`/api/folders/${f.id}`, { method: "DELETE" }))} aria-label={`Mappe ${f.name} löschen`} data-testid="plan-folder-remove">
                ✕
              </button>
            </span>
          </div>
          <label>
            Name
            <input defaultValue={f.name} maxLength={40} onBlur={(e) => e.target.value.trim() && e.target.value !== f.name && change(f, { name: e.target.value })} />
          </label>
          <label>
            Speisekarte
            <select value={f.menu} onChange={(e) => change(f, { menu: e.target.value })} data-testid="plan-folder-menu">
              {menuOptions}
            </select>
          </label>
          <label>
            Empfehlungskarte
            <select value={f.recommendations} onChange={(e) => change(f, { recommendations: e.target.value })} data-testid="plan-folder-recommendations">
              <option value="">keine</option>
              {data.menus
                .filter((m) => m.id !== f.menu)
                .map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.title}
                  </option>
                ))}
            </select>
          </label>
          <label>
            Getränke
            <select value={f.drinksFrom === "menu" && f.drinksMenu ? `menu:${f.drinksMenu}` : f.drinksFrom} onChange={(e) => change(f, e.target.value.startsWith("menu:") ? { drinksFrom: "menu", drinksMenu: e.target.value.slice(5) } : { drinksFrom: e.target.value as DrinksFrom, drinksMenu: "" })} data-testid="plan-folder-drinks">
              <option value="">die der Speisekarte</option>
              {data.settings.kellerlog && <option value="kellerlog">aus KellerLog</option>}
              <option value="none">keine</option>
              {data.menus
                .filter((m) => m.id !== f.menu)
                .map((m) => (
                  <option key={m.id} value={`menu:${m.id}`}>
                    feste Getränkekarte: {m.title}
                  </option>
                ))}
            </select>
          </label>
        </div>
      ))}
      {adding ? (
        <form
          className="folder-box"
          onSubmit={(e) => {
            e.preventDefault();
            void run(() => api("/api/folders", { body: { name: adding.name, menu: adding.menu || data.menus.find((m) => m.id !== pub?.id)?.id || data.menus[0]?.id } })).then((ok) => ok && setAdding(null));
          }}
          data-testid="plan-folder-new"
        >
          <label>
            Name der Mappe
            <input value={adding.name} maxLength={40} onChange={(e) => setAdding({ ...adding, name: e.target.value })} data-testid="plan-folder-name" />
          </label>
          <label>
            Speisekarte
            <select value={adding.menu || data.menus.find((m) => m.id !== pub?.id)?.id || ""} onChange={(e) => setAdding({ ...adding, menu: e.target.value })} data-testid="plan-folder-new-menu">
              {menuOptions}
            </select>
          </label>
          <div className="buttons">
            <button className="primary" type="submit" disabled={state.busy || !adding.name.trim() || data.menus.length === 0} data-testid="plan-folder-add">
              Mappe anlegen
            </button>
            {(data.folders ?? []).length > 0 && (
              <button type="button" onClick={() => setAdding(null)}>
                Abbrechen
              </button>
            )}
          </div>
        </form>
      ) : (
        <button onClick={() => setAdding({ name: "", menu: "" })} data-testid="plan-folder-more">
          + Mappe
        </button>
      )}
      {state.error && <p className="error-box">{state.error}</p>}
      <div className="buttons">
        <button className="primary" disabled={!(data.folders ?? []).length} onClick={onNext} data-testid="plan-next-week">
          Weiter: Wochenplan
        </button>
      </div>
    </section>
  );
}

/** Wochenplan (Bild 8): a row per weekday with its times, templates, „wie Vortag“, the day in words. */
function WeekStep({ data, rows, setRows, wrong, busy, onBack, onNext }: { data: Data; rows: DayRow[]; setRows: (fn: (r: DayRow[]) => DayRow[]) => void; wrong: string[]; busy: boolean; onBack: () => void; onNext: () => void }) {
  const folders = data.folders ?? [];
  const [tpl, setTpl] = useState(folders[0]?.id ?? "");
  const setDay = (d: number, fn: (r: DayRow) => DayRow) => setRows((all) => all.map((r, k) => (k === d ? fn(r) : r)));
  const add = (days: number[], t: Omit<DayTime, "folder">) => setRows((all) => all.map((r, k) => (days.includes(k) && !r.times.some((x) => x.folder === tpl && x.from === t.from && x.to === t.to) ? { ...r, times: [...r.times, { folder: tpl, ...t }] } : r)));
  return (
    <section className="card" data-testid="plan-step-week">
      <div className="row" style={{ flexWrap: "wrap", gap: "0.4rem", alignItems: "center" }}>
        <span className="small muted">Vorlage für</span>
        <select value={tpl} onChange={(e) => setTpl(e.target.value)} aria-label="Mappe der Vorlage" data-testid="plan-template-folder">
          {folders.map((f) => (
            <option key={f.id} value={f.id}>
              {f.name}
            </option>
          ))}
        </select>
        <button onClick={() => add([0, 1, 2, 3, 4], { from: 690, to: 870 })} data-testid="plan-template-lunch">
          Mo–Fr mittags
        </button>
        <button onClick={() => add([0, 1, 2, 3, 4, 5, 6], { from: 1020, to: 1380 })} data-testid="plan-template-evening">
          täglich abends
        </button>
      </div>
      <ul className="week-rows">
        {rows.map((r, d) => (
          <li key={d} data-testid={`plan-day-${d}`}>
            <div className="week-row-head">
              <b>{DAYS_LONG[d]}</b>
              <span className="row" style={{ gap: "0.6rem" }}>
                <label className="check small">
                  <input type="checkbox" checked={r.closed} onChange={(e) => setDay(d, (x) => ({ ...x, closed: e.target.checked }))} data-testid={`plan-day-${d}-closed`} />
                  Ruhetag
                </label>
                {d > 0 && (
                  <button className="link" onClick={() => setDay(d, () => ({ times: rows[d - 1]!.times.map((t) => ({ ...t })), closed: rows[d - 1]!.closed }))} data-testid={`plan-day-${d}-same`}>
                    wie {DAYS_LONG[d - 1]}
                  </button>
                )}
              </span>
            </div>
            {r.times.map((t, k) => (
              <div key={k} className="week-time">
                <select value={t.folder} onChange={(e) => setDay(d, (x) => ({ ...x, times: x.times.map((y, j) => (j === k ? { ...y, folder: e.target.value } : y)) }))} aria-label="Mappe">
                  {folders.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name}
                    </option>
                  ))}
                </select>
                <input type="time" value={clockText(t.from)} onChange={(e) => minutesOf(e.target.value) !== null && setDay(d, (x) => ({ ...x, times: x.times.map((y, j) => (j === k ? { ...y, from: minutesOf(e.target.value)! } : y)) }))} aria-label="von" />
                <input type="time" value={clockText(t.to % 1440)} onChange={(e) => minutesOf(e.target.value) !== null && setDay(d, (x) => ({ ...x, times: x.times.map((y, j) => (j === k ? { ...y, to: minutesOf(e.target.value)! } : y)) }))} aria-label="bis" />
                <button className="link danger" onClick={() => setDay(d, (x) => ({ ...x, times: x.times.filter((_, j) => j !== k) }))} aria-label="Zeit entfernen">
                  ✕
                </button>
              </div>
            ))}
            <button className="link" onClick={() => setDay(d, (x) => ({ ...x, times: [...x.times, { folder: tpl || folders[0]?.id || "", from: 690, to: 870 }] }))} data-testid={`plan-day-${d}-add`}>
              + Zeit
            </button>
            <p className="hint" style={{ margin: "0.2rem 0 0" }} data-testid={`plan-day-${d}-words`}>
              {dayWords(r.times, (f) => nameOf(f, data), r.closed)}
            </p>
          </li>
        ))}
      </ul>
      {wrong.length > 0 && (
        <div className="error-box" data-testid="plan-wrong">
          {wrong.map((w) => (
            <p key={w} style={{ margin: 0 }}>
              {w}
            </p>
          ))}
        </div>
      )}
      <div className="buttons">
        <button className="primary" disabled={busy || wrong.length > 0} onClick={onNext} data-testid="plan-next-check">
          Weiter: Prüfen
        </button>
        <button onClick={onBack}>Zurück</button>
      </div>
    </section>
  );
}

/** Prüfen (Bild 9): every day as its bar, the page at a day and time, what is wrong; „Fertig“. */
function CheckStep({ data, schedule, wrong, busy, onBack, onDone }: { data: Data; schedule: Schedule; wrong: string[]; busy: boolean; onBack: () => void; onDone: () => void }) {
  const today = localOf(Date.now()).date;
  const days = Array.from({ length: 7 }, (_, k) => dateAfter(today, k));
  const [day, setDay] = useState(days[1]!);
  const [time, setTime] = useState("12:00");
  const view: Data = { ...data, settings: { ...data.settings, schedule } };
  const unused = (data.folders ?? []).filter((f) => !schedule.rules.some((r) => r.folder === f.id));
  const minute = minutesOf(time) ?? 720;
  return (
    <section className="card" data-testid="plan-step-check">
      {days.map((date) => (
        <PlanBar key={date} data={view} date={date} label={`${DAYS_LONG[weekdayOf(date)]} ${Number(date.slice(8))}.${Number(date.slice(5, 7))}.`} />
      ))}
      <div className="row" style={{ flexWrap: "wrap", gap: "0.4rem", alignItems: "center", marginTop: "0.8rem" }}>
        <span>So sehen Gäste es am</span>
        <select value={day} onChange={(e) => setDay(e.target.value)} aria-label="Tag" data-testid="plan-preview-day">
          {days.map((d) => (
            <option key={d} value={d}>
              {DAYS_LONG[weekdayOf(d)]} {Number(d.slice(8))}.{Number(d.slice(5, 7))}.
            </option>
          ))}
        </select>
        <span>um</span>
        <input type="time" value={time} onChange={(e) => setTime(e.target.value)} aria-label="Uhrzeit" style={{ width: "auto" }} data-testid="plan-preview-time" />
        <a className="button" href={`/api/publish/preview?at=${atLocal(day, minute)}&workspace=${store.session!.workspaceId}`} target="_blank" rel="noopener" data-testid="plan-preview">
          Ansehen
        </a>
      </div>
      <p className="hint" data-testid="plan-preview-says">
        {DAYS_LONG[weekdayOf(day)]} um {uhr(minute)}: {nameOf(folderAt(schedule, data.settings.exceptions ?? [], atLocal(day, minute)).folder, data)}. Die Vorschau zeigt den Stand, der nach „Fertig“ gilt, sobald die Mappen übernommen sind.
      </p>
      {wrong.length > 0 && <div className="error-box">{wrong.join(" ")}</div>}
      {unused.map((f) => (
        <p key={f.id} className="warn">
          „{f.name}“ kommt im Wochenplan nicht vor; sie gilt nur über eine Ausnahme.
        </p>
      ))}
      <div className="buttons">
        <button className="primary" disabled={busy || wrong.length > 0} onClick={onDone} data-testid="plan-done">
          Fertig
        </button>
        <button onClick={onBack}>Zurück</button>
      </div>
    </section>
  );
}

/** Ausnahme für einen Tag (Z6, Bild 11): four choices one understands without a word more. */
export function ExceptionSheet({ data, onClose }: { data: Data; onClose: () => void }) {
  const folders = data.folders ?? [];
  const today = localOf(Date.now()).date;
  const [date, setDate] = useState(dateAfter(today, 1));
  const [kind, setKind] = useState<Exception["kind"]>("closed");
  const [like, setLike] = useState<Weekday>(6);
  const [only, setOnly] = useState(folders[0]?.id ?? OTHERWISE);
  const [times, setTimes] = useState<DayTime[]>([{ folder: folders[0]?.id ?? OTHERWISE, from: 690, to: 870 }]);
  const [state, setState] = useState<{ busy?: boolean; error?: string }>({});
  const e: Exception = kind === "closed" ? { date, kind } : kind === "like" ? { date, kind, like } : kind === "only" ? { date, kind, folder: only } : { date, kind, times };
  const view: Data = { ...data, settings: { ...data.settings, exceptions: [...(data.settings.exceptions ?? []).filter((x) => x.date !== date), e] } };
  async function save() {
    setState({ busy: true });
    try {
      await store.change(() => api("/api/schedule", { method: "PUT", body: { exceptions: view.settings.exceptions } }));
      onClose();
    } catch (err) {
      setState({ error: messageOf(err) });
    }
  }
  return (
    <div className="overlay" role="dialog" aria-label="Ausnahme für einen Tag" onClick={(x) => x.target === x.currentTarget && onClose()}>
      <div className="dialog" data-testid="exception-sheet">
        <h2>Ausnahme für einen Tag</h2>
        <label>
          Tag
          <input type="date" value={date} min={today} onChange={(x) => setDate(x.target.value)} data-testid="exception-date" />
        </label>
        <div className="when-choice" role="radiogroup" aria-label="Wie">
          <label className="check">
            <input type="radio" checked={kind === "closed"} onChange={() => setKind("closed")} data-testid="exception-closed" />
            Geschlossen
          </label>
          <label className="check">
            <input type="radio" checked={kind === "like"} onChange={() => setKind("like")} data-testid="exception-like" />
            Wie ein anderer Tag
            {kind === "like" && (
              <select value={like} onChange={(x) => setLike(Number(x.target.value) as Weekday)} style={{ width: "auto", marginLeft: "0.4rem" }} data-testid="exception-like-day">
                {DAYS_LONG.map((d, i) => (
                  <option key={d} value={i}>
                    wie {d}
                  </option>
                ))}
              </select>
            )}
          </label>
          <label className="check">
            <input type="radio" checked={kind === "only"} onChange={() => setKind("only")} data-testid="exception-only" />
            Den ganzen Tag eine Mappe
            {kind === "only" && (
              <select value={only} onChange={(x) => setOnly(x.target.value)} style={{ width: "auto", marginLeft: "0.4rem" }} data-testid="exception-only-folder">
                {foldersOf(data).map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
              </select>
            )}
          </label>
          <label className="check">
            <input type="radio" checked={kind === "own"} onChange={() => setKind("own")} data-testid="exception-own" />
            Eigene Zeiten
          </label>
        </div>
        {kind === "own" && (
          <div>
            {times.map((t, k) => (
              <div key={k} className="week-time">
                <select value={t.folder} onChange={(x) => setTimes((all) => all.map((y, j) => (j === k ? { ...y, folder: x.target.value } : y)))} aria-label="Mappe">
                  {folders.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name}
                    </option>
                  ))}
                </select>
                <input type="time" value={clockText(t.from)} onChange={(x) => minutesOf(x.target.value) !== null && setTimes((all) => all.map((y, j) => (j === k ? { ...y, from: minutesOf(x.target.value)! } : y)))} aria-label="von" />
                <input type="time" value={clockText(t.to % 1440)} onChange={(x) => minutesOf(x.target.value) !== null && setTimes((all) => all.map((y, j) => (j === k ? { ...y, to: minutesOf(x.target.value)! } : y)))} aria-label="bis" />
                <button className="link danger" onClick={() => setTimes((all) => all.filter((_, j) => j !== k))} aria-label="Zeit entfernen">
                  ✕
                </button>
              </div>
            ))}
            <button className="link" onClick={() => setTimes((all) => [...all, { folder: folders[0]?.id ?? OTHERWISE, from: 1020, to: 1380 }])}>
              + Zeit
            </button>
          </div>
        )}
        <p className="ok-box" data-testid="exception-say">
          Am {Number(date.slice(8))}.{Number(date.slice(5, 7))}. sehen Gäste {dayText(view, date)}. Am Tag danach verschwindet die Ausnahme von selbst.
        </p>
        {state.error && <p className="error-box">{state.error}</p>}
        <div className="buttons">
          <button className="primary" disabled={state.busy || !date} onClick={() => void save()} data-testid="exception-save">
            Speichern
          </button>
          <button onClick={onClose}>Abbrechen</button>
        </div>
      </div>
    </div>
  );
}
